import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { Colors } from '../constants/theme';
import { errorReporter } from '../services/errorReporter';

/**
 * RED DE SEGURIDAD DE LA APP.
 *
 * Hasta ahora Go Safely no tenía NINGUNA: cualquier excepción durante el
 * dibujado de una pantalla cerraba el APK en seco, sin diálogo y sin mensaje.
 * Para el usuario eso es indistinguible de "la app está rota", y para nosotros
 * era imposible saber qué falló porque no quedaba rastro en ningún sitio.
 *
 * Con esto, un error deja de ser un cierre: se muestra una pantalla que explica
 * qué pasó, ofrece volver a intentar, y guarda el detalle técnico por si hay que
 * diagnosticarlo. La app sigue viva.
 *
 * Nota: esto atrapa errores de RENDER de React. NO atrapa errores nativos (los
 * del propio Android) ni los de promesas sin `catch` — para eso está el
 * manejador global que se instala más abajo.
 */

type Props = { children: React.ReactNode };
type State = { error: Error | null; info: string };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, info: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Queda en el log del dispositivo: es lo que permite diagnosticar después.
    console.error('[ErrorBoundary] pantalla rota:', error?.message, info?.componentStack);
    this.setState({ info: String(info?.componentStack ?? '').slice(0, 900) });

    // ── Y AHORA TAMBIÉN SALE DEL TELÉFONO ─────────────────────────
    // Esto solo hacía `console.error`, y en un APK de release **no hay nadie
    // leyendo la consola**. `errorReporter` (Crashlytics) existía desde hacía
    // semanas y no lo llamaba ni un solo archivo del proyecto.
    //
    // Lo que costó: el cierre de `applyBaseMapPadding` estuvo registrado en
    // Crashlytics desde el 8 de septiembre — 19 cierres, 4 usuarios — y nos
    // enteramos dos días después, solo porque fuimos a mirar a mano. Los cierres
    // NATIVOS sí llegaban solos; los errores de React, que son estos, no llegaban
    // a ninguna parte.
    //
    // Envuelto en try/catch a propósito: un reportador de errores que revienta
    // dentro del manejador de errores deja al usuario sin ni siquiera la
    // pantalla de "algo falló".
    try {
      errorReporter.captureException(error, {
        tag: 'ErrorBoundary',
        severity: 'fatal',
        extra: { componentStack: String(info?.componentStack ?? '').slice(0, 900) },
      });
    } catch {
      /* si el reportador falla, la app sigue viva y con su pantalla */
    }
  }

  reintentar = () => this.setState({ error: null, info: '' });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children as React.ReactElement;

    return (
      <View style={{ flex: 1, backgroundColor: Colors.background, padding: 26, justifyContent: 'center' }}>
        <Text style={{ fontSize: 46, textAlign: 'center', marginBottom: 14 }}>😕</Text>
        <Text style={{ fontSize: 21, fontWeight: '800', color: Colors.text, textAlign: 'center', marginBottom: 10 }}>
          Algo falló en esta pantalla
        </Text>
        <Text style={{ fontSize: 14.5, color: Colors.textSecondary, textAlign: 'center', lineHeight: 21, marginBottom: 26 }}>
          Tus datos están a salvo y tu sesión sigue abierta. Puedes volver a
          intentarlo; si se repite, cierra la app y ábrela de nuevo.
        </Text>

        <TouchableOpacity
          onPress={this.reintentar}
          style={{ backgroundColor: Colors.primary, paddingVertical: 15, borderRadius: 13, alignItems: 'center' }}
        >
          <Text style={{ color: '#FFF', fontWeight: '800', fontSize: 15.5 }}>Volver a intentar</Text>
        </TouchableOpacity>

        {/* El detalle técnico solo se muestra en desarrollo: al usuario no le
            sirve de nada y solo lo asusta. */}
        {__DEV__ && (
          <ScrollView style={{ marginTop: 22, maxHeight: 220 }}>
            <Text style={{ color: Colors.textSecondary, fontSize: 11, fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' }}>
              {error.message}
              {'\n\n'}
              {this.state.info}
            </Text>
          </ScrollView>
        )}
      </View>
    );
  }
}

/**
 * Manejador global para lo que el ErrorBoundary NO puede atrapar: promesas
 * rechazadas sin `catch` y errores fuera del ciclo de render. Sin esto, un
 * `await` que falla dentro de un listener de Firestore también puede tumbar
 * la app en producción.
 */
export function instalarManejadorGlobal() {
  try {
    const g: any = globalThis as any;
    if (g.__gsErrorHandlerInstalado) return;
    g.__gsErrorHandlerInstalado = true;

    const anterior = g.ErrorUtils?.getGlobalHandler?.();
    g.ErrorUtils?.setGlobalHandler?.((err: any, esFatal?: boolean) => {
      console.error('[global] error no capturado', esFatal ? '(FATAL)' : '', err?.message ?? err);

      // Reportar ANTES de delegar: si el error es fatal, el manejador de abajo
      // termina cerrando el proceso, y después de eso ya no corre nada nuestro.
      try {
        errorReporter.captureException(err, {
          tag: 'global',
          severity: esFatal ? 'fatal' : 'error',
        });
      } catch {
        /* nunca romper el manejador de errores */
      }

      // Se delega al manejador original para no romper el comportamiento de
      // desarrollo (la pantalla roja de Metro).
      if (typeof anterior === 'function') anterior(err, esFatal);
    });
  } catch (e) {
    console.warn('[global] no se pudo instalar el manejador de errores:', e);
  }
}
